import {
  generateSecretKey, getPublicKey, bytesToHex, hexToBytes, npubEncode, nsecEncode, normalizeSecret,
} from '@herald/core';

/**
 * Browser-side key and state storage.
 *
 * Uses localStorage, which is honest about what it is: adequate for a proof of
 * concept, not adequate for a production wallet. A secp256k1 key cannot be made
 * non-extractable via WebCrypto, so a real app would keep it in the platform
 * keystore behind a biometric prompt (iOS Keychain / Android Keystore /
 * passkey-wrapped storage) and expose only a sign operation to the page.
 *
 * The UI states this to the user rather than implying safety it does not have.
 */

export class BrowserVault {
  constructor(storageKey) {
    this.storageKey = storageKey;
    this.state = this.#read();
  }

  #read() {
    try {
      const raw = localStorage.getItem(this.storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  save() {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.state));
      return true;
    } catch {
      // Private windows and blocked site data both land here.
      return false;
    }
  }

  get exists() { return !!this.state; }
  get secretKey() { return hexToBytes(this.state.secretKey); }
  get pubkey() { return this.state.pubkey; }
  get npub() { return npubEncode(this.state.pubkey); }
  get nsec() { return nsecEncode(this.secretKey); }

  create(extra = {}) {
    const secretKey = generateSecretKey();
    this.state = {
      version: 1,
      secretKey: bytesToHex(secretKey),
      pubkey: getPublicKey(secretKey),
      createdAt: Math.floor(Date.now() / 1000),
      ...extra,
    };
    this.save();
    return this.state;
  }

  /** Import an existing identity, so a key can be moved between devices. */
  importSecret(input, extra = {}) {
    // normalizeSecret accepts both nsec1... and raw hex, and throws on anything else.
    const secretKey = normalizeSecret(input.trim());
    this.state = {
      version: 1,
      secretKey: bytesToHex(secretKey),
      pubkey: getPublicKey(secretKey),
      createdAt: Math.floor(Date.now() / 1000),
      ...extra,
    };
    this.save();
    return this.state;
  }

  destroy() {
    this.state = null;
    try { localStorage.removeItem(this.storageKey); } catch { /* ignore */ }
  }
}
